import { type RequestPayload, router } from "@inertiajs/core";
import { usePage } from "@inertiajs/react";
import { Checkbox, Divider, Fieldset, Group as MantineGroup, Select, Stack, Text, TextInput } from "@mantine/core";
import { MIME_TYPES } from "@mantine/dropzone";
import { isNotEmpty, useForm } from "@mantine/form";
import { useState } from "react";
import { calculateFileSize } from "../../../helpers/fileHelper";
import { notEmptyMessage } from "../../../helpers/formHelper";
import type { Document } from "../../../models/document";
import { getOccupationOptions, type Occupation } from "../../../models/enums/occupation";
import { Role } from "../../../models/enums/role";
import { type Group, getGroupOptions } from "../../../models/group";
import { getYearContextOptions, type YearContext } from "../../../models/yearContext";
import { ViewConfigForm, type ViewConfigFormType } from "../../accessConfig/ViewConfigForm";
import { SaveButton } from "../../button/SaveButton";
import { DropZone } from "../../dropzone/Dropzone";
import { emailTemplateValidation } from "../../email/EmailEditorForm";
import { InfoCardPopover } from "../../infoCardPopover/InfoCardPopover";
import { BaseDocumentForm, type BaseDocumentFormType } from "../BaseDocumentForm";

type GlobalDocumentFormProps = {
  yearContexts: YearContext[];
  groups: Group[];
  document?: Document;
  onSubmit: (value: RequestPayload) => void;
  saveButtonLabel: string;
};

type GlobalDocumentFormType = BaseDocumentFormType &
  ViewConfigFormType & {
    file?: File;
    title?: string;
    occupation?: Occupation;
    year_context_id?: string;
    group_id?: string;
    publish: boolean;
  };

type FormErrors = {
  file?: string[];
};

export function GlobalDocumentForm({ yearContexts, groups, document, saveButtonLabel, onSubmit }: GlobalDocumentFormProps) {
  const form = useForm<GlobalDocumentFormType>({
    mode: "uncontrolled",
    initialValues: {
      file: document?.file,
      title: document?.title,
      occupation: document?.occupation,
      year_context_id: document?.year_context?.id.toString(),
      group_id: document?.group?.id.toString(),
      publish: document?.publish ?? false,
      email_template: document?.email_template,
      subject: document?.subject,
      send_mail: document?.send_mail ?? false,
      document_readable: document?.document_readable ?? [Role.APPRENTICE, Role.APPRENTICE_SUPERVISOR, Role.PARENT],
    },
    validate: {
      title: isNotEmpty(notEmptyMessage),
      subject: (value, values) => (values.send_mail ? isNotEmpty(notEmptyMessage)(value) : null),
      email_template: (value, values) => (values.send_mail ? emailTemplateValidation(value) : null),
    },
  });

  const { errors } = usePage<{ errors: FormErrors }>().props;
  const [groupSelected, setGroupSelected] = useState(!!form.values.group_id);

  form.watch("group_id", ({ value }) => setGroupSelected(!!value));
  form.watch("year_context_id", ({ value }) => reloadGroups(value, form.getValues().occupation));
  form.watch("occupation", ({ value }) => reloadGroups(form.getValues().year_context_id, value));
  const reloadGroups = (yearContextId?: string, occupation?: Occupation) =>
    router.reload({
      only: ["groups"],
      data: { selected_year_context_id: yearContextId, selected_occupation: occupation },
    });

  const selectedFile = (
    <>
      {form.getValues().file && (
        <Text>
          <b>{form.getValues().file?.name}</b> {calculateFileSize(form.getValues().file?.size)}
        </Text>
      )}
    </>
  );

  const viewConfigTitle = (
    <MantineGroup justify="center" align="center" gap="xs" w="100%">
      Einsehbar
      <InfoCardPopover>Lernende/r hat immer Zugriff auf die globalen Dokumente</InfoCardPopover>
    </MantineGroup>
  );

  return (
    <BaseDocumentForm form={form} onSubmit={onSubmit} saveButtonLabel={saveButtonLabel} disabled={document?.publish}>
      <Fieldset legend="Details">
        <Stack maw="60rem">
          <Select
            data={getYearContextOptions(yearContexts)}
            key={form.key("year_context_id")}
            {...form.getInputProps("year_context_id")}
            clearable
            label="Jahrgang"
            disabled={groupSelected || document?.publish}
          />
          <Select
            label="Beruf"
            clearable
            data={getOccupationOptions()}
            key={form.key("occupation")}
            {...form.getInputProps("occupation")}
            disabled={groupSelected || document?.publish}
          />
          <Select
            label="Gruppe"
            clearable
            nothingFoundMessage="Keine Gruppen..."
            data={getGroupOptions(groups ?? [])}
            key={form.key("group_id")}
            {...form.getInputProps("group_id")}
            disabled={document?.publish}
          />
          <TextInput withAsterisk label="Name" key={form.key("title")} {...form.getInputProps("title")} disabled={document?.publish} />
          <DropZone
            mimeTypes={[MIME_TYPES.pdf]}
            onDrop={(files) => form.setFieldValue("file", files[0])}
            multiple={false}
            error={errors?.file}
            disabled={document?.publish}
          />
          {selectedFile}

          <Checkbox label="Freigeben" key={form.key("publish")} {...form.getInputProps("publish", { type: "checkbox" })} />
          <ViewConfigForm form={form} disabled={document?.publish} title={viewConfigTitle} />
          <Divider />
          <SaveButton label={saveButtonLabel} />
        </Stack>
      </Fieldset>
    </BaseDocumentForm>
  );
}
